import "./turbo-rally.css";

export type KartDriverId = "nova" | "briggs" | "pip" | "sable" | "rex" | "luma";
export type KartItem = "boost" | "spark" | "slick" | "shield";
export type KartSnapshot = { playerId: string; x: number; y: number; angle: number; speed: number; lap: number; progress: number; checkpoint?: number; item: string | null; finishedAt: number | null; updatedAt: number };
export type KartEvent = { id: string; playerId: string; kind: KartItem; targetId?: string; x?: number; y?: number; createdAt: number };
export type KartRaceView = {
  trackId: string; laps: number; drivers: Record<string, KartDriverId>; racerIds: string[];
  racers: Record<string, KartSnapshot>; events: KartEvent[]; startAt: number | null; finishOrder: string[];
};
export type KartPlayer = { id: string; name: string; connected: boolean };

export const KART_DRIVERS: Array<{ id: KartDriverId; name: string; car: string; color: string; accent: string; speed: number; accel: number; handling: number; weight: string; specialty: string }> = [
  { id: "nova", name: "Nova", car: "Comet GT", color: "#ff4e7a", accent: "#fff3a8", speed: .86, accel: .86, handling: .82, weight: "Medium", specialty: "Balanced" },
  { id: "briggs", name: "Briggs", car: "Iron Howler", color: "#ff9f2d", accent: "#252332", speed: 1, accel: .66, handling: .54, weight: "Heavy", specialty: "Top speed" },
  { id: "pip", name: "Pip", car: "Pocket Rocket", color: "#50d6ff", accent: "#f7fffa", speed: .69, accel: 1, handling: 1, weight: "Light", specialty: "Easy control" },
  { id: "sable", name: "Sable", car: "Night Arrow", color: "#9f72ff", accent: "#6bffce", speed: .91, accel: .8, handling: .9, weight: "Medium", specialty: "Cornering" },
  { id: "rex", name: "Rex", car: "Rumble V8", color: "#64df65", accent: "#fff06c", speed: .96, accel: .7, handling: .61, weight: "Heavy", specialty: "Off-road" },
  { id: "luma", name: "Luma", car: "Solar Zip", color: "#ffe057", accent: "#ff6ca9", speed: .76, accel: .94, handling: .93, weight: "Light", specialty: "Quick recovery" },
];

export const KART_TRACKS = [
  { id: "neon_harbor", name: "Neon Harbor", kicker: "Waterside straights", sky: "#071b3b", ground: "#123b5b", road: "#263046", edge: "#55e9ff", accent: "#ff4eaa" },
  { id: "canyon_rush", name: "Canyon Rush", kicker: "Dusty switchbacks", sky: "#3c1730", ground: "#a84c31", road: "#41383a", edge: "#ffd15d", accent: "#ff6b39" },
  { id: "cloudtop_circuit", name: "Cloudtop Circuit", kicker: "Airborne sweepers", sky: "#4168c7", ground: "#a8d8ff", road: "#425071", edge: "#ffffff", accent: "#ffe76a" },
  { id: "frostbite_falls", name: "Frostbite Falls", kicker: "Slippery hairpins", sky: "#092d4c", ground: "#74c8d8", road: "#37495d", edge: "#d9ffff", accent: "#61f1ff" },
  { id: "volcano_run", name: "Volcano Run", kicker: "Hot, fast, unforgiving", sky: "#260d1a", ground: "#5e1b1b", road: "#352d32", edge: "#ff632f", accent: "#ffcb3d" },
] as const;

type Point = { x: number; y: number };
type Slick = Point & { id: string; life: number };
type RaceState = KartRaceView & { players: KartPlayer[] };
type Send = (message: object) => void;

const TAU = Math.PI * 2;
const TRACK_SCALE = 1.72;
const ITEM_BOX_INDICES = [48, 112, 178, 244, 310, 374];
const ITEM_BOX_COLLISION_RADIUS = 54;
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const angleDelta = (target: number, current: number) => Math.atan2(Math.sin(target - current), Math.cos(target - current));
const driverById = (id?: string) => KART_DRIVERS.find((driver) => driver.id === id) || KART_DRIVERS[0];

function makeTrack(id: string) {
  const points: Point[] = [];
  for (let index = 0; index < 420; index += 1) {
    const t = index / 420 * TAU;
    let x = 0; let y = 0;
    if (id === "canyon_rush") { const r = 1 + .13 * Math.sin(t * 3); x = Math.cos(t) * 560 * r; y = Math.sin(t) * 350 * (1 + .1 * Math.cos(t * 2)); }
    else if (id === "cloudtop_circuit") { x = Math.cos(t) * 530 + Math.cos(t * 2) * 85; y = Math.sin(t) * 330 + Math.sin(t * 3) * 72; }
    else if (id === "frostbite_falls") { x = Math.cos(t) * 510 + Math.cos(t * 3) * 105; y = Math.sin(t) * 350 + Math.sin(t * 2) * 55; }
    else if (id === "volcano_run") { const r = 1 + .16 * Math.sin(t * 4); x = Math.cos(t) * 490 * r; y = Math.sin(t) * 335 * r; }
    else { x = Math.cos(t) * 560 + Math.sin(t * 2) * 80; y = Math.sin(t) * 345; }
    points.push({ x: x * TRACK_SCALE, y: y * TRACK_SCALE });
  }
  return points;
}

function nearestPoint(points: Point[], x: number, y: number) {
  let index = 0; let distanceSq = Infinity;
  for (let candidate = 0; candidate < points.length; candidate += 1) {
    const dx = points[candidate].x - x; const dy = points[candidate].y - y; const next = dx * dx + dy * dy;
    if (next < distanceSq) { distanceSq = next; index = candidate; }
  }
  return { index, distance: Math.sqrt(distanceSq) };
}

export class TurboRallyGame {
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private send: Send;
  private me: string;
  private state: RaceState;
  private points: Point[];
  private local: KartSnapshot;
  private keys = new Set<string>();
  private held = { left: false, right: false, gas: false, brake: false };
  private joystick = { x: 0, y: 0, active: false, pointerId: -1 };
  private animation = 0;
  private lastFrame = 0;
  private lastSync = 0;
  private lastHud = 0;
  private previousProgress = 0;
  private nextCheckpoint = 1;
  private boostUntil = 0;
  private shieldUntil = 0;
  private stunnedUntil = 0;
  private item: KartItem | null = null;
  private boxTimes = new Map<number, number>();
  private processedEvents = new Set<string>();
  private slicks: Slick[] = [];
  private particles: Array<Point & { vx: number; vy: number; life: number; color: string }> = [];
  private dpr = 1;
  private resizeObserver: ResizeObserver;
  private finishedSent = false;

  constructor(root: HTMLElement, initial: RaceState, myId: string, send: Send) {
    this.root = root; this.state = initial; this.me = myId; this.send = send; this.points = makeTrack(initial.trackId);
    const gridIndex = Math.max(0, initial.racerIds.indexOf(myId));
    const startIndex = (this.points.length - 8 - gridIndex * 5 + this.points.length) % this.points.length;
    const start = this.points[startIndex]; const next = this.points[(startIndex + 1) % this.points.length];
    this.local = initial.racers[myId] || { playerId: myId, x: start.x, y: start.y, angle: Math.atan2(next.y - start.y, next.x - start.x), speed: 0, lap: 1, progress: startIndex / this.points.length, checkpoint: 1, item: null, finishedAt: null, updatedAt: Date.now() };
    this.previousProgress = this.local.progress;
    this.nextCheckpoint = clamp(this.local.checkpoint || 1, 1, 4);
    this.root.innerHTML = this.shell();
    this.canvas = this.root.querySelector("canvas")!; this.ctx = this.canvas.getContext("2d", { alpha: false })!;
    this.resizeObserver = new ResizeObserver(() => this.resize()); this.resizeObserver.observe(this.canvas.parentElement!);
    this.bind(); this.resize(); this.processEvents(); this.animation = requestAnimationFrame((time) => this.frame(time));
  }

  update(next: RaceState) {
    if (next.trackId !== this.state.trackId) this.points = makeTrack(next.trackId);
    this.state = next; this.processEvents();
  }

  destroy() {
    cancelAnimationFrame(this.animation); this.resizeObserver.disconnect();
    window.removeEventListener("keydown", this.onKeyDown); window.removeEventListener("keyup", this.onKeyUp);
    document.body.classList.remove("turbo-rally-open");
  }

  private shell() {
    document.body.classList.add("turbo-rally-open");
    return `<main class="kart-shell">
      <div class="kart-stage"><canvas aria-label="Turbo Car Rally race track"></canvas>
        <header class="kart-hud"><div class="kart-place"><strong id="kart-place">—</strong><span>PLACE</span></div><div class="kart-lap"><span id="kart-track-name"></span><strong id="kart-lap">LAP 1/${this.state.laps}</strong></div><div class="kart-room">ROOM <b>${this.escape(this.state.players.length ? "LIVE" : "")}</b></div></header>
        <div class="kart-countdown" id="kart-countdown"></div>
        <aside class="kart-rank" id="kart-rank"></aside>
        <div class="kart-speed"><i id="kart-speed-needle"></i><strong id="kart-speed">0</strong><span>KM/H</span></div>
        <button class="kart-item" id="kart-item" type="button" aria-label="Use power-up"><span>?</span><strong>ITEM</strong></button>
        <div class="kart-controls" aria-label="Race controls">
          <div class="kart-joystick" id="kart-joystick" role="application" aria-label="Analog direction joystick. Drag the control toward the direction you want the car to travel.">
            <span class="kart-joystick__rings" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
            <span class="kart-joystick__thumb" id="kart-joystick-thumb" aria-hidden="true"><i></i></span>
            <small>DRAG TO DRIVE</small>
          </div>
          <button class="kart-accelerator" data-control="gas" type="button" aria-label="Accelerate"><span>▲</span><strong>ACCEL</strong><small>HOLD</small></button>
        </div>
        <div class="kart-banner" id="kart-banner"></div>
        <div class="kart-desktop-help"><b>←/→</b> OR <b>A/D</b> STEER <b>SPACE</b> ACCELERATE <b>SHIFT</b> BRAKE <b>E</b> USE ITEM</div>
      </div></main>`;
  }

  private bind() {
    window.addEventListener("keydown", this.onKeyDown); window.addEventListener("keyup", this.onKeyUp);
    this.root.querySelector("#kart-item")!.addEventListener("pointerdown", (event) => { event.preventDefault(); this.useItem(); });
    this.root.querySelectorAll<HTMLButtonElement>("[data-control]").forEach((button) => {
      const key = button.dataset.control as keyof typeof this.held;
      const set = (value: boolean) => { this.held[key] = value; button.classList.toggle("is-held", value); };
      button.addEventListener("pointerdown", (event) => { event.preventDefault(); button.setPointerCapture(event.pointerId); set(true); });
      button.addEventListener("pointerup", () => set(false)); button.addEventListener("pointercancel", () => set(false)); button.addEventListener("lostpointercapture", () => set(false));
    });
    const joystick = this.root.querySelector<HTMLElement>("#kart-joystick")!;
    joystick.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      this.joystick.active = true;
      this.joystick.pointerId = event.pointerId;
      joystick.setPointerCapture(event.pointerId);
      joystick.classList.add("is-active");
      this.moveJoystick(event);
    });
    joystick.addEventListener("pointermove", (event) => { if (this.joystick.active && event.pointerId === this.joystick.pointerId) this.moveJoystick(event); });
    const releaseJoystick = (event: PointerEvent) => {
      if (event.pointerId !== this.joystick.pointerId) return;
      this.joystick = { x: 0, y: 0, active: false, pointerId: -1 };
      joystick.classList.remove("is-active");
      this.positionJoystickThumb();
    };
    joystick.addEventListener("pointerup", releaseJoystick);
    joystick.addEventListener("pointercancel", releaseJoystick);
    joystick.addEventListener("lostpointercapture", releaseJoystick);
  }

  private moveJoystick(event: PointerEvent) {
    const pad = this.root.querySelector<HTMLElement>("#kart-joystick")!;
    const bounds = pad.getBoundingClientRect();
    const dx = event.clientX - (bounds.left + bounds.width / 2);
    const dy = event.clientY - (bounds.top + bounds.height / 2);
    const radius = bounds.width * .31;
    const magnitude = Math.hypot(dx, dy);
    const scale = magnitude > radius ? radius / magnitude : 1;
    this.joystick.x = clamp(dx * scale / radius, -1, 1);
    this.joystick.y = clamp(dy * scale / radius, -1, 1);
    this.positionJoystickThumb();
  }

  private positionJoystickThumb() {
    const thumb = this.root.querySelector<HTMLElement>("#kart-joystick-thumb");
    if (!thumb) return;
    thumb.style.setProperty("--stick-x", `${this.joystick.x * 44}px`);
    thumb.style.setProperty("--stick-y", `${this.joystick.y * 44}px`);
  }

  private onKeyDown = (event: KeyboardEvent) => { this.keys.add(event.code); if (event.code === "KeyE") { event.preventDefault(); this.useItem(); } if (event.code === "Space") event.preventDefault(); };
  private onKeyUp = (event: KeyboardEvent) => { this.keys.delete(event.code); };

  private resize() {
    const bounds = this.canvas.parentElement!.getBoundingClientRect(); this.dpr = Math.min(2, devicePixelRatio || 1);
    this.canvas.width = Math.max(1, Math.floor(bounds.width * this.dpr)); this.canvas.height = Math.max(1, Math.floor(bounds.height * this.dpr));
    this.canvas.style.width = `${bounds.width}px`; this.canvas.style.height = `${bounds.height}px`;
  }

  private frame(time: number) {
    const dt = Math.min(.034, Math.max(0, (time - (this.lastFrame || time)) / 1000)); this.lastFrame = time;
    this.step(dt, time); this.draw(time);
    if (time - this.lastHud > 100) { this.updateHud(time); this.lastHud = time; }
    if (time - this.lastSync > 80) { this.sync(); this.lastSync = time; }
    this.animation = requestAnimationFrame((next) => this.frame(next));
  }

  private step(dt: number, time: number) {
    const startAt = this.state.startAt || 0; const started = Date.now() >= startAt;
    const driver = driverById(this.state.drivers[this.me]);
    const keyboardSteering = Number(this.keys.has("ArrowRight") || this.keys.has("KeyD")) - Number(this.keys.has("ArrowLeft") || this.keys.has("KeyA"));
    const joystickSteering = Math.abs(this.joystick.x) > .16 ? this.joystick.x : 0;
    const steering = clamp(keyboardSteering + joystickSteering, -1, 1);
    const hasDirectionInput = Math.abs(steering) > .05;
    const gas = this.held.gas || this.keys.has("Space");
    const brake = this.held.brake || this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
    const maxSpeed = 255 + driver.speed * 165 + (time < this.boostUntil ? 155 : 0);
    if (!started || this.local.finishedAt) this.local.speed *= Math.pow(.92, dt * 60);
    else if (time < this.stunnedUntil) this.local.speed *= Math.pow(.91, dt * 60);
    else {
      if (gas) this.local.speed += (105 + driver.accel * 125) * dt;
      if (brake) this.local.speed -= 255 * dt;
      if (!gas && !brake) this.local.speed *= Math.pow(.989, dt * 60);
      this.local.speed = clamp(this.local.speed, -85, maxSpeed);
      if (hasDirectionInput) {
        const turnRate = (2.4 + driver.handling * 2.25) * (.62 + Math.min(1, Math.abs(this.local.speed) / 220) * .38);
        this.local.angle += steering * turnRate * dt;
      }
    }
    this.local.x += Math.cos(this.local.angle) * this.local.speed * dt; this.local.y += Math.sin(this.local.angle) * this.local.speed * dt;
    const nearest = nearestPoint(this.points, this.local.x, this.local.y);
    const center = this.points[nearest.index];
    const tangent = this.points[(nearest.index + 2) % this.points.length];
    const tangentAngle = Math.atan2(tangent.y - center.y, tangent.x - center.x);
    const forwardAlignment = Math.cos(angleDelta(tangentAngle, this.local.angle));
    if (started && !this.local.finishedAt && this.local.speed > 35 && forwardAlignment > -.2) {
      const assist = (hasDirectionInput ? .075 : .2) * clamp(.35 + nearest.distance / 115, .35, 1.4);
      this.local.angle += angleDelta(tangentAngle, this.local.angle) * assist * dt;
    }
    if (nearest.distance > 118) {
      const offRoadEase = driver.id === "rex" ? .993 : .982; this.local.speed *= Math.pow(offRoadEase, dt * 60);
      const pull = nearest.distance > 155 ? .55 : .22;
      this.local.x += (center.x - this.local.x) * dt * pull; this.local.y += (center.y - this.local.y) * dt * pull;
      if (nearest.distance > 235) { this.local.x += (center.x - this.local.x) * dt * 1.15; this.local.y += (center.y - this.local.y) * dt * 1.15; }
    }
    const progress = nearest.index / this.points.length;
    const movingForward = this.local.speed > 20 && forwardAlignment > .35;
    if (started && !this.local.finishedAt && movingForward) {
      if (this.nextCheckpoint === 1 && progress >= .2 && progress < .36) this.nextCheckpoint = 2;
      else if (this.nextCheckpoint === 2 && progress >= .45 && progress < .61) this.nextCheckpoint = 3;
      else if (this.nextCheckpoint === 3 && progress >= .7 && progress < .86) this.nextCheckpoint = 4;
      if (this.previousProgress > .9 && progress < .1) {
        if (this.nextCheckpoint === 4) {
          this.local.lap += 1;
          this.nextCheckpoint = 1;
          if (this.local.lap > this.state.laps) { this.local.finishedAt = Date.now(); this.finishedSent = false; this.banner("FINISH!", "Race complete"); }
          else this.banner(`LAP ${this.local.lap}`, "Full circuit complete");
        } else {
          this.nextCheckpoint = 1;
          this.banner("KEEP RACING", "Complete the full circuit");
        }
      }
    }
    this.previousProgress = progress; this.local.progress = progress; this.local.checkpoint = this.nextCheckpoint; this.local.item = this.item;
    this.checkItemBoxes(time); this.checkSlicks(dt, time);
    for (const particle of this.particles) { particle.x += particle.vx * dt; particle.y += particle.vy * dt; particle.life -= dt; }
    this.particles = this.particles.filter((particle) => particle.life > 0); this.slicks.forEach((slick) => slick.life -= dt); this.slicks = this.slicks.filter((slick) => slick.life > 0);
  }

  private checkItemBoxes(time: number) {
    if (this.item || Date.now() < (this.state.startAt || 0)) return;
    for (const box of ITEM_BOX_INDICES) {
      const point = this.points[box];
      const hit = Math.hypot(this.local.x - point.x, this.local.y - point.y) <= ITEM_BOX_COLLISION_RADIUS;
      if (hit && time - (this.boxTimes.get(box) ?? -10_000) > 5_000) {
        this.boxTimes.set(box, time); const rank = this.rankings().findIndex((entry) => entry.id === this.me);
        const pool: KartItem[] = rank > Math.floor(this.state.racerIds.length / 2) ? ["boost", "boost", "spark", "shield", "slick"] : ["slick", "shield", "boost", "spark"];
        this.item = pool[Math.floor(Math.random() * pool.length)]; this.local.item = this.item; this.banner(this.itemName(this.item), "Power-up ready"); this.burst(this.local.x, this.local.y, "#62f5ff", 18);
      }
    }
  }

  private useItem() {
    if (!this.item || Date.now() < (this.state.startAt || 0) || this.local.finishedAt) return;
    const kind = this.item; const id = `${this.me}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const payload: Record<string, unknown> = { type: "kart_item", kind, eventId: id };
    if (kind === "boost") { this.boostUntil = performance.now() + 1_900; this.burst(this.local.x, this.local.y, "#ffdc58", 25); }
    if (kind === "shield") { this.shieldUntil = performance.now() + 5_500; }
    if (kind === "slick") { payload.x = this.local.x - Math.cos(this.local.angle) * 38; payload.y = this.local.y - Math.sin(this.local.angle) * 38; this.slicks.push({ id, x: payload.x as number, y: payload.y as number, life: 16 }); }
    if (kind === "spark") { const target = this.targetAhead(); if (target) payload.targetId = target; else { this.boostUntil = performance.now() + 1_200; payload.kind = "boost"; } }
    this.processedEvents.add(id); this.send(payload); this.item = null; this.local.item = null;
  }

  private targetAhead() { const ranking = this.rankings(); const index = ranking.findIndex((entry) => entry.id === this.me); return index > 0 ? ranking[index - 1].id : ranking.find((entry) => entry.id !== this.me)?.id; }

  private processEvents() {
    for (const event of this.state.events) {
      if (this.processedEvents.has(event.id)) continue; this.processedEvents.add(event.id);
      if (event.kind === "spark" && event.targetId === this.me) {
        if (performance.now() < this.shieldUntil) { this.shieldUntil = 0; this.banner("BLOCKED!", "Shield absorbed the spark"); }
        else { this.stunnedUntil = performance.now() + 1_150; this.local.speed *= .45; this.banner("ZAPPED!", "Steer through it"); this.burst(this.local.x, this.local.y, "#fff36b", 24); }
      }
      if (event.kind === "slick" && typeof event.x === "number" && typeof event.y === "number" && Date.now() - event.createdAt < 16_000) this.slicks.push({ id: event.id, x: event.x, y: event.y, life: Math.max(.1, 16 - (Date.now() - event.createdAt) / 1000) });
    }
  }

  private checkSlicks(dt: number, time: number) {
    if (time < this.shieldUntil || time < this.stunnedUntil) return;
    const hit = this.slicks.find((slick) => Math.hypot(slick.x - this.local.x, slick.y - this.local.y) < 29);
    if (hit) { this.stunnedUntil = time + 850; this.local.angle += 2.4; hit.life = 0; this.banner("SLIPPED!", "Counter-steer"); }
  }

  private sync() {
    if (this.local.finishedAt && this.finishedSent) return;
    this.send({ type: "kart_sync", x: this.local.x, y: this.local.y, angle: this.local.angle, speed: this.local.speed, lap: this.local.lap, progress: this.local.progress, checkpoint: this.nextCheckpoint, item: this.item, finishedAt: this.local.finishedAt });
    if (this.local.finishedAt) this.finishedSent = true;
  }

  private rankings() {
    return this.state.racerIds.map((id) => {
      const snapshot = id === this.me ? this.local : this.state.racers[id];
      const finishIndex = this.state.finishOrder.indexOf(id);
      return { id, score: finishIndex >= 0 ? 10_000 - finishIndex : ((snapshot?.lap || 1) - 1) + (snapshot?.progress || 0), finishIndex };
    }).sort((a, b) => b.score - a.score);
  }

  private updateHud(time: number) {
    const rankings = this.rankings(); const place = rankings.findIndex((entry) => entry.id === this.me) + 1;
    const placeEl = this.root.querySelector("#kart-place")!; placeEl.textContent = this.ordinal(place);
    this.root.querySelector("#kart-lap")!.textContent = this.local.lap > this.state.laps ? "FINISHED" : `LAP ${this.local.lap}/${this.state.laps}`;
    this.root.querySelector("#kart-track-name")!.textContent = KART_TRACKS.find((track) => track.id === this.state.trackId)?.name || "Turbo Car Rally";
    const kph = Math.max(0, Math.round(Math.abs(this.local.speed) * .72)); this.root.querySelector("#kart-speed")!.textContent = String(kph);
    (this.root.querySelector<HTMLElement>("#kart-speed-needle")!).style.transform = `rotate(${-120 + clamp(kph / 310, 0, 1) * 240}deg)`;
    const itemButton = this.root.querySelector<HTMLButtonElement>("#kart-item")!; const item = this.item;
    itemButton.classList.toggle("has-item", Boolean(item)); itemButton.classList.toggle("has-shield", time < this.shieldUntil);
    itemButton.querySelector("span")!.textContent = item ? this.itemIcon(item) : time < this.shieldUntil ? "◉" : "?"; itemButton.querySelector("strong")!.textContent = item ? this.itemName(item) : time < this.shieldUntil ? "SHIELD" : "ITEM";
    const names = new Map(this.state.players.map((player) => [player.id, player.name]));
    this.root.querySelector("#kart-rank")!.innerHTML = rankings.slice(0, 6).map((entry, index) => `<div class="${entry.id === this.me ? "is-me" : ""}"><b>${index + 1}</b><i style="--kart-color:${driverById(this.state.drivers[entry.id]).color}"></i><span>${this.escape(names.get(entry.id) || "Racer")}</span>${entry.finishIndex >= 0 ? "<em>✓</em>" : ""}</div>`).join("");
    const countdown = this.root.querySelector<HTMLElement>("#kart-countdown")!; const remaining = (this.state.startAt || 0) - Date.now();
    countdown.textContent = remaining > 0 ? String(Math.max(1, Math.ceil(remaining / 1000))) : remaining > -850 ? "GO!" : ""; countdown.classList.toggle("is-go", remaining <= 0);
  }

  private draw(time: number) {
    const width = this.canvas.width / this.dpr; const height = this.canvas.height / this.dpr; const ctx = this.ctx; const track = KART_TRACKS.find((candidate) => candidate.id === this.state.trackId) || KART_TRACKS[0];
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0); ctx.fillStyle = track.sky; ctx.fillRect(0, 0, width, height);
    const scale = clamp(Math.min(width / 720, height / 560), .55, 1.08); ctx.save(); ctx.translate(width / 2, height / 2); ctx.scale(scale, scale); ctx.translate(-this.local.x, -this.local.y);
    ctx.fillStyle = track.ground; ctx.fillRect(this.local.x - width / scale, this.local.y - height / scale, width * 2 / scale, height * 2 / scale);
    this.drawScenery(ctx, track, time); this.strokeTrack(ctx, track.edge, 286); this.strokeTrack(ctx, track.road, 258); this.strokeTrack(ctx, "rgba(255,255,255,.15)", 5, [20, 23]);
    const start = this.points[0]; const next = this.points[3]; const angle = Math.atan2(next.y - start.y, next.x - start.x); ctx.save(); ctx.translate(start.x, start.y); ctx.rotate(angle); for (let y = -104; y < 104; y += 26) for (let x = -12; x < 13; x += 12) { ctx.fillStyle = ((x / 12 + y / 26) & 1) ? "#fff" : "#151520"; ctx.fillRect(x, y, 12, 26); } ctx.restore();
    for (const index of ITEM_BOX_INDICES) this.drawBox(ctx, this.points[index], time + index * 10);
    for (const slick of this.slicks) { ctx.fillStyle = "rgba(12,8,20,.8)"; ctx.beginPath(); ctx.ellipse(slick.x, slick.y, 28, 15, .3, 0, TAU); ctx.fill(); ctx.strokeStyle = "#8657c7"; ctx.lineWidth = 3; ctx.stroke(); }
    const snapshots = this.state.racerIds.map((id) => id === this.me ? this.local : this.state.racers[id]).filter((snapshot): snapshot is KartSnapshot => Boolean(snapshot));
    for (const snapshot of snapshots) this.drawKart(ctx, snapshot, driverById(this.state.drivers[snapshot.playerId]), snapshot.playerId === this.me, time);
    for (const particle of this.particles) { ctx.globalAlpha = clamp(particle.life * 2, 0, 1); ctx.fillStyle = particle.color; ctx.fillRect(particle.x, particle.y, 5, 5); } ctx.globalAlpha = 1; ctx.restore();
    const gradient = ctx.createRadialGradient(width / 2, height / 2, Math.min(width, height) * .2, width / 2, height / 2, Math.max(width, height) * .7); gradient.addColorStop(0, "transparent"); gradient.addColorStop(1, "rgba(4,5,18,.42)"); ctx.fillStyle = gradient; ctx.fillRect(0, 0, width, height);
  }

  private strokeTrack(ctx: CanvasRenderingContext2D, color: string, width: number, dash: number[] = []) { ctx.beginPath(); ctx.moveTo(this.points[0].x, this.points[0].y); for (let i = 1; i < this.points.length; i += 1) ctx.lineTo(this.points[i].x, this.points[i].y); ctx.closePath(); ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.setLineDash(dash); ctx.stroke(); ctx.setLineDash([]); }

  private drawScenery(ctx: CanvasRenderingContext2D, track: typeof KART_TRACKS[number], time: number) {
    const seedPoints = this.points.filter((_, index) => index % 28 === 0);
    for (let index = 0; index < seedPoints.length; index += 1) { const point = seedPoints[index]; const center = nearestPoint(this.points, point.x, point.y); const tangent = this.points[(center.index + 2) % this.points.length]; const angle = Math.atan2(tangent.y - point.y, tangent.x - point.x) + Math.PI / 2; const side = index % 2 ? 1 : -1; const x = point.x + Math.cos(angle) * 175 * side; const y = point.y + Math.sin(angle) * 175 * side; ctx.fillStyle = index % 3 ? track.accent : track.edge; ctx.globalAlpha = .35 + .1 * Math.sin(time / 500 + index); ctx.beginPath(); ctx.arc(x, y, 9 + index % 4 * 3, 0, TAU); ctx.fill(); } ctx.globalAlpha = 1;
  }

  private drawBox(ctx: CanvasRenderingContext2D, point: Point, time: number) { ctx.save(); ctx.translate(point.x, point.y); ctx.rotate(time / 700); ctx.fillStyle = "rgba(85,239,255,.32)"; ctx.strokeStyle = "#aaffff"; ctx.lineWidth = 4; ctx.fillRect(-18, -18, 36, 36); ctx.strokeRect(-18, -18, 36, 36); ctx.rotate(-time / 350); ctx.fillStyle = "#fff"; ctx.font = "900 24px system-ui"; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("?", 0, 0); ctx.restore(); }

  private drawKart(ctx: CanvasRenderingContext2D, snapshot: KartSnapshot, driver: ReturnType<typeof driverById>, isMe: boolean, time: number) {
    ctx.save(); ctx.translate(snapshot.x, snapshot.y); ctx.rotate(snapshot.angle); if (isMe && time < this.shieldUntil) { ctx.strokeStyle = "rgba(105,255,244,.85)"; ctx.lineWidth = 5; ctx.beginPath(); ctx.arc(0, 0, 31 + Math.sin(time / 90) * 2, 0, TAU); ctx.stroke(); }
    if (isMe && time < this.boostUntil) { ctx.fillStyle = "#ffe24f"; ctx.beginPath(); ctx.moveTo(-26, -9); ctx.lineTo(-48 - Math.random() * 18, 0); ctx.lineTo(-26, 9); ctx.fill(); }
    ctx.fillStyle = "#111522"; for (const y of [-18, 18]) { ctx.fillRect(-17, y - 6, 16, 12); ctx.fillRect(10, y - 6, 16, 12); }
    ctx.fillStyle = driver.color; ctx.strokeStyle = isMe ? "#fff" : "rgba(255,255,255,.5)"; ctx.lineWidth = isMe ? 4 : 2; ctx.beginPath(); ctx.roundRect(-25, -18, 50, 36, 10); ctx.fill(); ctx.stroke(); ctx.fillStyle = driver.accent; ctx.fillRect(5, -14, 12, 28); ctx.fillStyle = "#171827"; ctx.beginPath(); ctx.arc(-3, 0, 10, 0, TAU); ctx.fill(); ctx.fillStyle = driver.color; ctx.beginPath(); ctx.arc(-3, 0, 6, 0, TAU); ctx.fill(); ctx.restore();
  }

  private burst(x: number, y: number, color: string, count: number) { for (let i = 0; i < count; i += 1) { const angle = Math.random() * TAU; const speed = 45 + Math.random() * 150; this.particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: .35 + Math.random() * .55, color }); } }
  private banner(title: string, subtitle: string) { const el = this.root.querySelector<HTMLElement>("#kart-banner"); if (!el) return; el.innerHTML = `<strong>${this.escape(title)}</strong><span>${this.escape(subtitle)}</span>`; el.classList.remove("is-showing"); requestAnimationFrame(() => el.classList.add("is-showing")); window.setTimeout(() => el.classList.remove("is-showing"), 1700); }
  private ordinal(value: number) { return `${value}${value === 1 ? "ST" : value === 2 ? "ND" : value === 3 ? "RD" : "TH"}`; }
  private itemName(item: KartItem) { return ({ boost: "TURBO BOLT", spark: "HOMING SPARK", slick: "VOID SLICK", shield: "AURA SHIELD" })[item]; }
  private itemIcon(item: KartItem) { return ({ boost: "⚡", spark: "✦", slick: "●", shield: "◉" })[item]; }
  private escape(value: string) { return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]!); }
}
